import { type SVGProps } from 'react';
import { twMerge } from 'tailwind-merge';
import type { IconName } from './icons/name';
import iconSprite from './icons/sprite.svg';
export { IconName, iconSprite };

export const iconSizeClasses = Object.freeze({
  font: 'size-[1em]',
  'font-lg': 'size-[1.25em]',
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-7',
  '2xl': 'size-8',
  '3xl': 'size-10',
  '4xl': 'size-12',
  '5xl': 'size-16',
});
export type IconSize = keyof typeof iconSizeClasses;
export const childrenSizeClassName = {
  font: 'gap-1',
  'font-lg': 'gap-1',
  xs: 'gap-1',
  sm: 'gap-1',
  md: 'gap-2',
  lg: 'gap-2',
  xl: 'gap-3',
  '2xl': 'gap-3',
  '3xl': 'gap-3',
  '4xl': 'gap-3',
  '5xl': 'gap-3',
} satisfies Record<IconSize, string>;

/**
 * Renders an SVG icon. The icon defaults to the size of the font. To make it
 * align vertically with neighboring text, you need to wrap the icon and text
 * in a common parent and set the parent to display "flex" (or "inline-flex").
 * Alternatively, if you're ok with the icon being to the left of the text,
 * you can pass the text as a child of the icon and it will be automatically
 * aligned.
 */
export function Icon({
  name,
  size = 'lg',
  className,
  children,
  ...props
}: SVGProps<SVGSVGElement> & {
  name: IconName;
  size?: IconSize;
}) {
  if (children) {
    return (
      <span
        className={`inline-flex items-center ${childrenSizeClassName[size]}`}
      >
        <Icon name={name} size={size} className={className} {...props} />
        {children}
      </span>
    );
  }
  return (
    <svg
      {...props}
      className={twMerge(
        iconSizeClasses[size],
        'inline self-center flex-shrink-0',
        className
      )}
    >
      <use href={`${iconSprite}#${name}`} />
    </svg>
  );
}
